import type { AudioBusId } from "../ports/types";

/** How long a bus takes to switch or change volume — short enough to feel instant, long enough not to click. */
const BUS_RAMP_SECONDS = 0.03;

type BusNodes = {
  volume: GainNode;
  duck: GainNode;
};

export class AudioMixer {
  private readonly master: GainNode;
  private readonly buses: Record<AudioBusId, BusNodes>;

  private readonly busEnabled: Record<AudioBusId, boolean> = {
    music: true,
    sfx: true,
    voice: true,
    ui: true,
    ambient: true,
  };

  private readonly busVolume: Record<AudioBusId, number> = {
    music: 1,
    sfx: 1,
    voice: 1,
    ui: 1,
    ambient: 1,
  };

  private readonly busDuck: Record<AudioBusId, number> = {
    music: 1,
    sfx: 1,
    voice: 1,
    ui: 1,
    ambient: 1,
  };

  constructor(private readonly ctx: AudioContext) {
    this.master = ctx.createGain();
    this.master.gain.value = 1;
    this.master.connect(ctx.destination);

    this.buses = {
      music: this.createBus(),
      sfx: this.createBus(),
      voice: this.createBus(),
      ui: this.createBus(),
      ambient: this.createBus(),
    };

    for (const bus of Object.keys(this.buses) as AudioBusId[]) {
      this.buses[bus].duck.connect(this.master);
      this.recomputeBus(bus);
    }
  }

  public setBusEnabled(bus: AudioBusId, enabled: boolean): void {
    this.busEnabled[bus] = enabled;
    this.recomputeBus(bus);
  }

  public isBusEnabled(bus: AudioBusId): boolean {
    return this.busEnabled[bus];
  }

  public setBusVolume(bus: AudioBusId, volume: number): void {
    this.busVolume[bus] = Math.max(0, volume);
    this.recomputeBus(bus);
  }

  /**
   * Master gain (0–1) applied uniformly across all buses. Sits in front of
   * each bus's individual volume + duck stages — game-side `busVolumes`
   * config (e.g. ambient at 0.6) stays intact and is scaled by this.
   *
   * Uses a short `linearRampToValueAtTime` (default 30ms) so rapid slider
   * drags don't produce audible clicks. `rampMs = 0` writes the level at
   * once; no caller passes it — a mute, `muteAudioOnStart`'s included, ramps too.
   */
  public setMasterVolume(volume: number, rampMs = 30): void {
    const clamped = Math.max(0, Math.min(1, volume));
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);

    if (rampMs <= 0) {
      this.master.gain.setValueAtTime(clamped, now);
      return;
    }

    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(clamped, now + rampMs / 1000);
  }

  public setBusDuck(bus: AudioBusId, duck: number, fadeMs = 0): void {
    const clamped = Math.max(0, duck);
    this.busDuck[bus] = clamped;

    const node = this.buses[bus].duck;
    const now = this.ctx.currentTime;

    node.gain.cancelScheduledValues(now);

    if (fadeMs <= 0) {
      node.gain.setValueAtTime(clamped, now);
      return;
    }

    node.gain.setValueAtTime(node.gain.value, now);
    node.gain.linearRampToValueAtTime(clamped, now + fadeMs / 1000);
  }

  public getBusInputNode(bus: AudioBusId): GainNode {
    return this.buses[bus].volume;
  }

  private createBus(): BusNodes {
    const volume = this.ctx.createGain();
    const duck = this.ctx.createGain();
    volume.connect(duck);

    return { volume, duck };
  }

  /**
   * The bus's volume stage, ramped rather than written: until 2026-09-26 switching music off or on
   * jumped a playing track's level in one sample, which can click.
   */
  private recomputeBus(bus: AudioBusId): void {
    const effectiveVolume = this.busEnabled[bus] ? this.busVolume[bus] : 0;
    const gain = this.buses[bus].volume.gain;
    const now = this.ctx.currentTime;
    const level = gain.value;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(level, now);
    gain.linearRampToValueAtTime(effectiveVolume, now + BUS_RAMP_SECONDS);
  }
}